import React, { useEffect, useState, Fragment } from 'react';
import { motion } from 'framer-motion';
import { Clock, Users, Flame } from 'lucide-react';
const DEADLINE_KEY = 'pretty-pets-deadline';
export function getDeadline(): number {
  const stored = typeof window !== 'undefined' ? localStorage.getItem(DEADLINE_KEY) : null;
  if (stored) {
    const ts = parseInt(stored, 10);
    if (!Number.isNaN(ts) && ts > Date.now()) return ts;
  }
  const deadline = Date.now() + 24 * 60 * 60 * 1000;
  if (typeof window !== 'undefined') localStorage.setItem(DEADLINE_KEY, String(deadline));
  return deadline;
}
export interface TimeLeft {
  hours: number;
  minutes: number;
  seconds: number;
}
export function getTimeLeft(deadline: number): TimeLeft {
  const diff = Math.max(0, deadline - Date.now());
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor(diff / (1000 * 60) % 60);
  const seconds = Math.floor(diff / 1000 % 60);
  return {
    hours,
    minutes,
    seconds
  };
}
export function pad(n: number): string {
  return n.toString().padStart(2, '0');
}
export function useCountdown(): TimeLeft {
  const [deadline] = useState<number>(() => getDeadline());
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() => getTimeLeft(deadline));
  useEffect(() => {
    const id = setInterval(() => {
      setTimeLeft(getTimeLeft(deadline));
    }, 1000);
    return () => clearInterval(id);
  }, [deadline]);
  return timeLeft;
}
export function MiniCountdown() {
  const t = useCountdown();
  return <div className="inline-flex items-center gap-1.5 text-[0.7rem] sm:text-xs font-bold" data-id="element-54">
      <Flame size={12} className="text-brand-rose shrink-0" data-id="element-55" />
      <span className="uppercase tracking-wider text-brand-rose hidden sm:inline" data-id="element-56">
        Oferta termina en
      </span>
      <span className="sm:hidden uppercase tracking-wider text-brand-rose" data-id="element-57">
        Oferta:
      </span>
      <span className="flex items-center gap-0.5 font-mono tabular-nums" data-id="element-58">
        <span className="bg-brand-rose text-white px-1.5 py-0.5 rounded" data-id="element-59">
          {pad(t.hours)}
        </span>
        <span className="text-brand-rose" data-id="element-60">:</span>
        <span className="bg-brand-rose text-white px-1.5 py-0.5 rounded" data-id="element-61">
          {pad(t.minutes)}
        </span>
        <span className="text-brand-rose" data-id="element-62">:</span>
        <span className="bg-brand-rose text-white px-1.5 py-0.5 rounded" data-id="element-63">
          {pad(t.seconds)}
        </span>
      </span>
    </div>;
}
export function CountdownUrgency() {
  const timeLeft = useCountdown();
  const stockClaimed = 87;
  const stockLeft = 100 - stockClaimed;
  const units: {
    label: string;
    value: number;
  }[] = [{
    label: 'Horas',
    value: timeLeft.hours
  }, {
    label: 'Minutos',
    value: timeLeft.minutes
  }, {
    label: 'Segundos',
    value: timeLeft.seconds
  }];
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="max-w-2xl mx-auto mb-10 bg-white rounded-3xl shadow-soft-pink border border-brand-pink-light p-6 md:p-8" data-id="element-64">
      <div className="flex items-center justify-center gap-2 mb-4" data-id="element-65">
        <Flame className="text-brand-rose" size={20} data-id="element-66" />
        <span className="text-sm md:text-base font-bold uppercase tracking-wider text-brand-rose" data-id="element-67">
          La oferta termina en
        </span>
        <Flame className="text-brand-rose" size={20} data-id="element-68" />
      </div>

      <div className="flex items-center justify-center gap-2 md:gap-4 mb-6" data-id="element-69">
        {units.map((unit, i) => <Fragment key={unit.label} data-id="element-70">
            <div className="flex flex-col items-center" data-id="element-71">
              <div className="bg-gradient-to-br from-brand-pink-light to-brand-pink-mid text-brand-brown font-serif font-bold text-3xl md:text-5xl rounded-2xl shadow-sm w-16 md:w-24 h-16 md:h-24 flex items-center justify-center border border-brand-pink-mid/50" data-id="element-72">
                {pad(unit.value)}
              </div>
              <span className="text-[0.65rem] md:text-xs font-medium uppercase tracking-wider text-brand-brown/70 mt-2" data-id="element-73">
                {unit.label}
              </span>
            </div>
            {i < units.length - 1 && <span className="text-brand-rose font-bold text-2xl md:text-4xl pb-6" data-id="element-74">
                :
              </span>}
          </Fragment>)}
      </div>

      <div className="border-t border-brand-pink-light/60 pt-5" data-id="element-75">
        <div className="flex items-center justify-between mb-2 text-xs md:text-sm" data-id="element-76">
          <span className="flex items-center gap-1.5 font-medium text-brand-brown/80" data-id="element-77">
            <Users size={16} className="text-brand-rose" data-id="element-78" />
            {stockClaimed}% ya lo compraron
          </span>
          <span className="font-bold text-brand-rose" data-id="element-79">
            ¡Solo queda {stockLeft}%!
          </span>
        </div>
        <div className="relative w-full h-3 bg-brand-cream-mid rounded-full overflow-hidden" data-id="element-80">
          <motion.div initial={{
          width: 0
        }} whileInView={{
          width: `${stockClaimed}%`
        }} viewport={{
          once: true
        }} transition={{
          duration: 1.2,
          ease: 'easeOut'
        }} className="absolute inset-y-0 left-0 bg-gradient-to-r from-brand-pink-dark to-brand-rose rounded-full" data-id="element-81" />
          <motion.div animate={{
          opacity: [0.4, 1, 0.4]
        }} transition={{
          duration: 1.5,
          repeat: Infinity
        }} className="absolute inset-y-0 right-[12%] w-1 bg-white/80 rounded-full" data-id="element-82" />
        </div>
        <p className="text-center text-xs text-brand-brown/60 mt-2 flex items-center justify-center gap-1" data-id="element-83">
          <Clock size={12} data-id="element-84" /> Quedan pocas unidades a este precio
        </p>
      </div>
    </motion.div>;
}